/**
 * Pantalla de cámara de la marcación (004 · US1 · T029; US6 · T068; US7 · T072; FR-002, FR-005,
 * FR-032, FR-035; design-system §8.1 · "Flujo de marcación (cámara)").
 *
 * Pantalla completa, fondo negro de la cámara y sin barra de navegación. De arriba abajo: texto
 * guía ("Apunta tu cámara a Fermin"), marco de enfoque estático (QA-4 de la 005: sin anillo que
 * gire; solo una barra fina de progreso bajo el marco durante "Verificando…"), texto de estado y
 * el botón ghost "Cancelar".
 *
 * - El texto de estado vive en una región `aria-live="polite"` (§10) y **nunca** lleva detalles
 *   técnicos: "Buscando marcador…", "Verificando…", "✓ Listo". Ni identificadores de challenge, ni
 *   conteos de frames, ni nonces (FR-032).
 * - La ayuda de los 8 s dice **"Separa un poco el teléfono y encuadra el código en el marco."** —
 *   nunca "acércate": por debajo de 20 cm la lente gran angular de los iPhone Pro recientes no
 *   enfoca y no hay forma de corregirlo desde la web (research R-5). A los 20 s se ofrece
 *   reintentar.
 * - El aviso de ubicación (`locationNotice`, T068) es **texto plano, no modal**: nunca bloquea la
 *   marcación ni añade un botón. Igual que `help`/`suggestSupport`, es puramente informativo.
 * - **T079 (revisión de accesibilidad, contraste AA).** Todo el texto de esta pantalla —el
 *   principal y las líneas secundarias de cola/ayuda/soporte/ubicación/reintento— es
 *   `text-white` **opaco**, nunca atenuado (`/80` o similar): el velo `rgba(20,28,43,0.55)` de
 *   `FocusFrame` es la única garantía de contraste sobre un fondo que es la cámara en vivo (no se
 *   puede controlar su color), y atenuar el texto además del velo puede bajar el contraste real
 *   por debajo de AA cuando la cámara ve algo claro. Antes de este cambio las líneas secundarias
 *   usaban `text-white/80`; se corrigió aquí (no había ninguna razón del design-system para esa
 *   opacidad, y medirla contra un fondo claro no llegaba a 4.5:1).
 *
 * - La espera en cola (US4/T049) es **una línea más**, no una pantalla: el `<video>` sigue montado
 *   y capturando, que es lo que permite que el turno se aproveche sin volver a tocar nada.
 * - El reintento automático del modo autónomo (T087/FR-045) tampoco es una pantalla: reemplaza el
 *   texto de estado principal por un mensaje de espera honesto ("Un momento… vamos a intentarlo de
 *   nuevo.") y la barra de progreso desaparece, con la cámara viva exactamente igual que en
 *   `QUEUED`. `data-retry` en la raíz permite comprobarlo sin depender del texto traducido.
 *
 * - **T096 · el marco dice la verdad sobre el recorte.** El lado del marco sale de `roi`, que es
 *   el ROI que el bucle de captura está usando ahora mismo, convertido a píxeles CSS con
 *   `roiOverlaySide` (el `<video>` va con `object-fit: cover`). Y el marco está anclado al
 *   **centro exacto del viewport**, que es donde está el recorte: por eso el marco no vive en el
 *   flujo `justify-between` como antes —las líneas de cola, ayuda, soporte y ubicación lo
 *   empujaban hacia arriba al aparecer, descentrándolo respecto del recorte— sino en un ancla
 *   `absolute inset-0` centrada, con los textos colgando **de él**. Cambiar esta disposición sin
 *   mantener el centrado vuelve a desincronizar la guía del recorte.
 *
 * `data-testid`: `marking-camera`, `marking-video`, `marking-frame`, `marking-frame-anchor`,
 * `marking-frame-lines`, `marking-progress` (solo en "Verificando…"), `marking-status`, `marking-help`, `marking-retry`,
 * `marking-cancel`, `marking-queue`, `marking-support`, `marking-location-notice`.
 */
import { Button } from '../../components/ui/button';
import { t } from '../../i18n';

import { FocusFrame } from './focus-frame';
import { useRoiOverlaySide } from './roi-overlay';

import type { MarkingPhase } from './machine';
import type { ScanRoi } from '../../camera';
import type { RefObject } from 'react';

/** Texto de estado por fase (§9: sin jerga, sin detalles técnicos). */
export function statusTextFor(phase: MarkingPhase): string {
  switch (phase) {
    case 'OPENING':
      return t('attendance.status.opening');
    case 'SEARCHING':
      return t('attendance.status.searching');
    case 'QUEUED':
      return t('attendance.status.queued');
    case 'REQUESTING':
    case 'CHALLENGE':
    case 'VERIFYING':
      return t('attendance.status.verifying');
    case 'SUCCESS':
      return t('attendance.status.ready');
    default:
      return '';
  }
}

/**
 * Detalle de la espera en cola (US4 · T049). `null` fuera de `QUEUED`. La posición y la estimación
 * las decide el servidor (`202 QUEUED`, `queue_position` del stream o la señal de espera); aquí solo
 * se pintan.
 */
export interface QueueHint {
  position: number;
  etaMs: number;
}

/** Texto de acompañamiento de la espera: primero la posición, después la estimación. */
export function queueHintText(queue: QueueHint): string {
  const head = queue.position <= 1 ? t('attendance.queue.next') : t('attendance.queue.ahead', { ahead: queue.position - 1 });
  const seconds = Math.max(1, Math.round(queue.etaMs / 1000));
  return `${head} ${t('attendance.queue.eta', { seconds })}`;
}

export interface CameraStageProps {
  phase: MarkingPhase;
  videoRef: RefObject<HTMLVideoElement | null>;
  /** US4/T049: posición y espera estimada mientras el marcador atiende a otro. */
  queue?: QueueHint | null;
  /** 0–100 ("Verificando…", barra fina bajo el marco) o `undefined` (sin barra). */
  progress: number | undefined;
  /** FR-005: ayuda a los 8 s. */
  help: boolean;
  /** FR-005: reintento ofrecido a los 20 s. */
  retryOffered: boolean;
  /** FR-035/US7 AC4: aviso tras tres fallos seguidos en 2 minutos. */
  suggestSupport: boolean;
  /** US6/T068: aviso no modal, una vez por sesión, cuando no hubo ubicación. Nunca bloquea. */
  locationNotice: boolean;
  /** T087/FR-045: `true` mientras se espera el reintento automático del modo autónomo. */
  autonomousRetry?: boolean;
  /** T096: recorte vigente del bucle de captura. El marco se dibuja **desde aquí**. */
  roi?: ScanRoi | null;
  onRetry: () => void;
  onCancel: () => void;
}

export function CameraStage({
  phase,
  videoRef,
  queue = null,
  progress,
  help,
  retryOffered,
  suggestSupport,
  locationNotice,
  autonomousRetry = false,
  roi = null,
  onRetry,
  onCancel,
}: CameraStageProps) {
  // T096: el lado del marco es el del recorte real, convertido a píxeles CSS.
  const frameSide = useRoiOverlaySide(roi);
  // T087/FR-045: el mensaje de espera del reintento automático reemplaza al de estado normal —
  // nunca aparecen los dos a la vez, y nunca se nombra la causa técnica (anti-replay, slots).
  const status = autonomousRetry ? t('attendance.status.autonomousRetry') : statusTextFor(phase);

  return (
    <div
      data-testid="marking-camera"
      data-phase={phase}
      {...(autonomousRetry ? { 'data-retry': 'true' } : {})}
      className="fixed inset-0 overflow-hidden bg-black"
    >
      <video
        ref={videoRef}
        data-testid="marking-video"
        aria-label={t('attendance.camera.preview')}
        muted
        playsInline
        autoPlay
        className="absolute inset-0 h-full w-full object-cover"
      />

      <p className="fm-safe-top absolute inset-x-0 top-0 z-10 px-fm-4 pt-fm-8 text-center text-fm-lg font-semibold text-white">
        {t('attendance.guide.aim')}
      </p>

      {/* T096: ancla en el centro **exacto** del viewport, que es donde está el recorte central del
          fotograma. Nada del flujo puede moverla, y los textos cuelgan del marco en vez de
          empujarlo. `pointer-events-none` para no tapar los botones de abajo. */}
      <div
        data-testid="marking-frame-anchor"
        className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
      >
        <div className="relative">
        <FocusFrame
          data-testid="marking-frame"
          label={status === '' ? t('attendance.status.searching') : status}
          {...(progress === undefined ? {} : { progress })}
          filled={phase === 'SUCCESS'}
          sidePx={frameSide}
          roiFraction={roi === null ? null : roi.fraction}
        />

        <div
          data-testid="marking-frame-lines"
          className="absolute top-full left-1/2 flex w-screen max-w-[100vw] -translate-x-1/2 flex-col items-center gap-fm-3 px-fm-4 pt-fm-4"
        >
        <p
          data-testid="marking-status"
          aria-live="polite"
          className="min-h-fm-6 text-center text-fm-base text-white transition-opacity duration-fm-fast motion-reduce:transition-none"
        >
          {status}
        </p>

        {/* US4/T049: la espera **no** reemplaza la cámara; es una línea más bajo el marco, con el
            `<video>` vivo detrás. En cuanto el marcador empiece a pintar, los frames entran solos y
            la fase pasa a `CHALLENGE` sin que el trabajador toque nada. */}
        {phase === 'QUEUED' ? (
          <p
            data-testid="marking-queue"
            {...(queue === null ? {} : { 'data-position': queue.position })}
            aria-live="polite"
            className="text-center text-fm-base text-white"
          >
            {queue === null ? t('attendance.status.queued') : queueHintText(queue)}
          </p>
        ) : null}

        {help ? (
          <p data-testid="marking-help" className="text-center text-fm-base text-white">
            {t('attendance.guide.help')}
          </p>
        ) : null}

        {suggestSupport ? (
          <p data-testid="marking-support" className="text-center text-fm-base text-white">
            {t('attendance.guide.support')}
          </p>
        ) : null}

        {locationNotice ? (
          <p data-testid="marking-location-notice" className="text-center text-fm-base text-white">
            {t('attendance.location.unavailableNotice')}
          </p>
        ) : null}
        </div>
        </div>
      </div>

      <div className="fm-safe-bottom-gutter absolute inset-x-0 bottom-0 z-10 flex w-full flex-col gap-fm-3 px-fm-4">
        {retryOffered ? (
          <Button data-testid="marking-retry" variant="secondary" onClick={onRetry}>
            {t('attendance.actions.retry')}
          </Button>
        ) : null}
        <Button
          data-testid="marking-cancel"
          variant="ghost"
          className="text-white hover:bg-white/10 active:bg-white/20"
          onClick={onCancel}
        >
          {t('attendance.actions.cancel')}
        </Button>
      </div>
    </div>
  );
}
